import React from "react";
import { BlockStack, Box, ContextualSaveBar, Grid, Page } from "@shopify/polaris";
import { BarChart3, Bot, CreditCard, Paintbrush, Sparkles } from "lucide-react";
import { ReachLogo } from "../../Logo";
import type { SettingsTab } from "../constants";

type SettingsLayoutProps = {
  activeTabId: string;
  children: React.ReactNode;
  density: "comfortable" | "compact";
  dirtyMessage: string;
  hero?: { title: string; subtitle: string; insight?: string; icon: React.ReactNode };
  isDirty: boolean;
  loading: boolean;
  onDiscard: () => void;
  onSave: () => void;
  safeSelectedTab: number;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
  tabs: SettingsTab[];
  variant?: SettingsLayoutVariant;
  visualEditor: React.ReactNode;
};

export type SettingsLayoutVariant = "dashboard" | "headless";

const TAB_ICONS: Record<string, React.ReactNode> = {
  "visual-editor": <Paintbrush size={17} />,
  assistant: <Bot size={17} />,
  analytics: <BarChart3 size={17} />,
  plans: <CreditCard size={17} />,
  designer: <Sparkles size={17} />,
};

export function SettingsLayout({
  activeTabId,
  children,
  density,
  dirtyMessage,
  hero,
  isDirty,
  loading,
  onDiscard,
  onSave,
  safeSelectedTab,
  setSelectedTab,
  tabs,
  variant = "dashboard",
  visualEditor,
}: SettingsLayoutProps) {
  const isHeadless = variant === "headless";
  const workspaceHeight = isHeadless ? "calc(100vh - 112px)" : "calc(100vh - 145px)";
  const workspaceMinHeight = isHeadless ? "560px" : "650px";

  const shellMarkup = (
    <div
      className={`theme-settings-shell${isHeadless ? " theme-settings-shell--headless" : ""}`}
      data-density={density}
      data-variant={variant}
    >
      <div className="theme-settings-header" data-variant={variant}>
        {!isHeadless && (
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: "1.25rem",
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.12)",
                borderRadius: "12px",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ReachLogo size="large" showText={false} color="rgba(255,255,255,0.95)" detailColor="#124a66" />
            </div>
            <span
              style={{
                fontSize: "26px",
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-0.04em",
                lineHeight: 1,
                fontFamily: "'Inter', -apple-system, sans-serif",
              }}
            >
              REACH<span className="theme-wordmark-dot" style={{ fontSize: "32px", lineHeight: 0 }}>.</span>
            </span>
          </div>
        )}
        <nav style={{ display: "flex", gap: "0.3rem", flex: 1, justifyContent: isHeadless ? "flex-start" : "center" }}>
          {tabs.map((tab, index) => {
            const isActive = safeSelectedTab === index;
            return (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(index)}
                className="theme-settings-tab"
                data-active={isActive ? "true" : "false"}
                style={{ fontWeight: isActive ? 600 : 450 }}
              >
                {TAB_ICONS[tab.id] || null}
                {tab.content}
              </button>
            );
          })}
        </nav>
      </div>

      <div
        style={{
          borderRadius: "0 0 12px 12px",
          overflow: "hidden",
          height: workspaceHeight,
          minHeight: workspaceMinHeight,
          display: tabs[safeSelectedTab].id === "visual-editor" ? "flex" : "none",
          flexDirection: "column",
        }}
      >
        {visualEditor}
      </div>

      {tabs[safeSelectedTab].id !== "visual-editor" && (
        <div
          className="theme-content-wrap"
          data-tab={activeTabId}
          style={{
            padding: isHeadless ? "1rem" : "1.25rem",
            minHeight: workspaceHeight,
            borderRadius: "0 0 12px 12px",
          }}
        >
          {hero && (
            <div className="theme-tab-hero" data-tab={activeTabId}>
              <div className="theme-tab-hero-inner">
                <span className="theme-tab-hero-icon">{hero.icon}</span>
                <div>
                  <h2 className="theme-tab-hero-title">{hero.title}</h2>
                  <p className="theme-tab-hero-subtitle">{hero.subtitle}</p>
                </div>
              </div>
              {hero.insight && <span className="theme-tab-hero-chip">{hero.insight}</span>}
            </div>
          )}
          <BlockStack gap={density === "compact" ? "300" : "400"}>
            <Grid>
              <Grid.Cell columnSpan={{ xs: 6, lg: 12 }}>
                <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
                  <Box minHeight="600px">{children}</Box>
                </div>
              </Grid.Cell>
            </Grid>
            <Box paddingBlockEnd="800" />
          </BlockStack>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isDirty && (
        <ContextualSaveBar
          message={dirtyMessage}
          saveAction={{ onAction: onSave, loading }}
          discardAction={{ onAction: onDiscard }}
        />
      )}
      {isHeadless ? shellMarkup : <Page fullWidth>{shellMarkup}</Page>}
    </>
  );
}
